import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import type { CumulativeBundleSegment, CumulativeDomainSegment } from '@openx/types/gqlTypes.ts';
import { copyToClipboardWithConfirmation } from '@openx/utils/lib/copyToClipboard.ts';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealPrompt } from '../../../hooks/useDealPrompt.ts';
import { downloadAsCsv } from '../../../utils/downloadAsCsv.ts';
import { dealPromptContextTypes } from '../../../utils/types.ts';
import {
  formatItemForFullListCsvDownload,
  formatItemForItemsOnlyCsvDownload,
  getFullDataSetCsvFilename,
  getItemList,
  getItemsOnlyCsvFilename,
} from './utils.ts';

export const DealPromptDetailsDownloadMenu = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { selectedItems, selectedTile, contextType },
  } = useDealPrompt();

  const item = selectedTile?.item as CumulativeDomainSegment | CumulativeBundleSegment;

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  const [menuAnchorElement, setMenuAnchorElement] = useState<null | HTMLElement>(null);

  const handleMenuClose = useCallback(() => setIsMenuOpen(false), []);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setIsMenuOpen(true);
    setMenuAnchorElement(event.currentTarget);
  }, []);

  const handleCopyDomainsOnlyToClipboard = useCallback(() => {
    if (!item) return;

    const lines = getItemList({
      item,
      selectedItems,
      contextType,
    });

    const text = lines.join('\n');

    copyToClipboardWithConfirmation(text, enqueueSnackbar, t);

    handleMenuClose();
  }, [item, selectedItems, t, handleMenuClose, contextType, enqueueSnackbar]);

  const handleDownloadItemsOnly = useCallback(() => {
    if (!item) return;
    const formattedData = formatItemForItemsOnlyCsvDownload({
      item,
      selectedItems,
      marketRequestsLabel: t('Market requests'),
      impressionsLabel: t('Impressions'),
      contextType,
    });

    downloadAsCsv(formattedData, getItemsOnlyCsvFilename(contextType));

    handleMenuClose();
  }, [item, handleMenuClose, t, selectedItems, contextType]);

  const handleDownloadFullDataset = useCallback(() => {
    if (!item) return;
    const formattedData = formatItemForFullListCsvDownload({ item, selectedItems, contextType });
    downloadAsCsv(formattedData, getFullDataSetCsvFilename(contextType));
    handleMenuClose();
  }, [item, handleMenuClose, selectedItems, contextType]);

  const menuStructure = useMemo(
    () => [
      {
        label: t('Copy to Clipboard'),
        extraLabel: t('Ready for targeting'),
        onClick: handleCopyDomainsOnlyToClipboard,
        dataTest: 'copy-to-clipboard',
      },
      {
        label: contextType === dealPromptContextTypes.DOMAINS ? t('Export Domains List') : t('Export Apps List'),
        onClick: handleDownloadItemsOnly,
        dataTest: 'export-results-list',
      },
      {
        label: t('Export Full Information'),
        onClick: handleDownloadFullDataset,
        dataTest: 'export-full-information',
      },
    ],
    [t, handleDownloadItemsOnly, handleDownloadFullDataset, contextType, handleCopyDomainsOnlyToClipboard]
  );

  const Icon = isMenuOpen ? ExpandLess : ExpandMore;

  return (
    <>
      <Button
        data-test="export-menu"
        data-pendo-feature={`deal-prompt-details-${contextType}-export`}
        onClick={handleMenuOpen}
      >
        <Stack direction="row" spacing={1} useFlexGap>
          {t('Export')}
          <Icon sx={{ width: '18px', height: '18px' }} />
        </Stack>
      </Button>
      <Menu anchorEl={menuAnchorElement} open={isMenuOpen} onClose={handleMenuClose}>
        <Stack
          spacing="2px"
          sx={{
            height: '100%',
            px: 1.5,
          }}
        >
          {menuStructure.map(({ label, extraLabel, onClick, dataTest }, itemIndex) => (
            <MenuItem
              key={`${label}-${itemIndex}`}
              onClick={onClick}
              data-test={dataTest}
              sx={{
                p: 0,
                display: 'flex',
                gap: 0.5,
                alignItems: 'start',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: 'text.primary',
                },
              }}
            >
              <Typography variant="body1">{t(label)}</Typography>

              {extraLabel && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'success.main',
                    fontWeight: 'fontWeightBold',
                    fontSize: '10px',
                    textTransform: 'uppercase',
                  }}
                >
                  {extraLabel}
                </Typography>
              )}
            </MenuItem>
          ))}
        </Stack>
      </Menu>
    </>
  );
};
